import React, { useState, useCallback, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, Loader2, Globe, Lock, Filter, Plus, X } from "lucide-react";
import api from "lib/api";
import ResultsMap from "components/ResultsMap";
import { flagEmoji } from "lib/country-centroids";

// tags: pílula com borda. DNS = verde suave, cert = âmbar (cadeado), porta = azul.
const DNS_CHIP = "inline-flex items-center gap-1 rounded-md border border-emerald-400/20 bg-emerald-400/5 px-2 py-0.5 text-xs font-medium text-emerald-200/80";
const CERT_CHIP = "inline-flex items-center gap-1 rounded-md border border-amber-400/20 bg-amber-400/5 px-2 py-0.5 text-xs font-medium text-amber-200/80";
const PORT_CHIP = "inline-flex items-center gap-1 rounded-md border border-blue-500/40 bg-blue-500/10 px-2 py-0.5 text-xs font-mono font-medium text-blue-300";

// flags de enriquecimento (cloud/saas/datacenter) — cor por tipo.
const FLAG_CHIP = {
  cloud: "border-sky-500/30 bg-sky-500/10 text-sky-300",
  saas: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  datacenter: "border-orange-500/30 bg-orange-500/10 text-orange-300",
};
const FLAG_FIELD = { cloud: "cloud_product", saas: "saas_product", datacenter: "datacenter" };

// Seção do card: título em negrito + tags (sem divisor/cor).
function CardSection({ title, children }) {
  return (
    <div className="mt-3">
      <div className="mb-1 text-sm font-bold">{title}</div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

// Aspas em valores com espaço/caracteres especiais (para os dorks).
function quote(v) {
  return `"${String(v).replace(/"/g, '\\"')}"`;
}

// ── Barra de filtros ──────────────────────────────────────────────────────
// Campos oferecidos no editor (chave da dork -> rótulo PT). "" = texto livre.
const FILTER_FIELDS = [
  { v: "", label: "Texto livre" },
  { v: "ip", label: "IP / CIDR" },
  { v: "port", label: "Porta" },
  { v: "hostname", label: "Hostname" },
  { v: "fqdn", label: "FQDN" },
  { v: "ssl", label: "Certificado (SSL)" },
  { v: "service", label: "Serviço" },
  { v: "product", label: "Produto" },
  { v: "version", label: "Versão" },
  { v: "cpe", label: "CPE" },
  { v: "banner", label: "Banner" },
  { v: "ostype", label: "Sistema (OS)" },
  { v: "country", label: "País" },
  { v: "region", label: "Estado" },
  { v: "city", label: "Cidade" },
  { v: "asn", label: "ASN" },
  { v: "cloud_product", label: "Cloud" },
  { v: "saas_product", label: "SaaS" },
  { v: "datacenter", label: "Datacenter" },
  { v: "cloud_provider", label: "Cloud (provider)" },
  { v: "cloud_region", label: "Cloud (região)" },
  { v: "cloud_service", label: "Cloud (serviço)" },
];
const FIELD_LABEL = Object.fromEntries(FILTER_FIELDS.map((f) => [f.v, f.label]));
const fieldLabel = (field) =>
  field == null ? "Expressão" : (FIELD_LABEL[field] ?? field);

// precisa de aspas: valor com espaço, parênteses ou aspas.
const needsQuote = (v) => /[\s()"]/.test(v);
function unquote(v) {
  const s = String(v).trim();
  if (s.length >= 2 && s[0] === '"' && s[s.length - 1] === '"') {
    return s.slice(1, -1).replace(/\\"/g, '"');
  }
  return s;
}

// Divide a query nos " AND " de nível superior (respeita aspas e parênteses).
function splitAnd(s) {
  const parts = [];
  let buf = "", depth = 0, inQ = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '"') { inQ = !inQ; buf += c; continue; }
    if (!inQ && c === "(") { depth++; buf += c; continue; }
    if (!inQ && c === ")") { depth = Math.max(0, depth - 1); buf += c; continue; }
    if (!inQ && depth === 0 && s.slice(i, i + 5).toUpperCase() === " AND ") {
      parts.push(buf); buf = ""; i += 4; continue;
    }
    buf += c;
  }
  if (buf.trim()) parts.push(buf);
  return parts.map((p) => p.trim()).filter(Boolean);
}

// Converte uma clause em {raw, neg, field, value, complex}. Clauses com OR/NOT/
// parênteses ficam "complex" (chip só removível, não editável).
function parseClause(raw) {
  let neg = false;
  let s = raw.trim();
  if (s.startsWith("-")) { neg = true; s = s.slice(1).trim(); }
  const complex = /\sOR\s|\sNOT\s|[()]/i.test(s);
  if (complex) return { raw, neg: false, field: null, value: raw, complex: true };
  const m = /^([a-zA-Z_][a-zA-Z0-9_]*)\s*:\s*([\s\S]+)$/.exec(s);
  if (m) return { raw, neg, field: m[1].toLowerCase(), value: unquote(m[2]) };
  return { raw, neg, field: "", value: unquote(s) };
}

function parseFilters(query) {
  if (!query || !query.trim()) return [];
  return splitAnd(query).map(parseClause);
}

function buildClause(f) {
  if (f.complex) return f.raw;
  const v = needsQuote(f.value) ? quote(f.value) : f.value;
  return (f.neg ? "-" : "") + (f.field ? `${f.field}:${v}` : v);
}
const buildQuery = (filters) => filters.map(buildClause).join(" AND ");

// FilterEditor: popover ancorado (estilo Kibana) reusado p/ adicionar e editar.
// `initial` presente => modo edição. Fecha com clique-fora/Esc. onSave({field,value,neg}).
function FilterEditor({ trigger, initial, onSave }) {
  const isEdit = !!initial;
  const [open, setOpen] = useState(false);
  const [field, setField] = useState("");
  const [value, setValue] = useState("");
  const [neg, setNeg] = useState(false);
  const ref = useRef(null);

  // (re)inicializa o form ao abrir: edição recarrega do `initial`; add zera.
  useEffect(() => {
    if (!open) return;
    if (initial) { setField(initial.field ?? ""); setValue(initial.value || ""); setNeg(!!initial.neg); }
    else { setField(""); setValue(""); setNeg(false); }
  }, [open, initial]);

  const close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open, close]);

  const canSave = value.trim() !== "";
  const submit = () => {
    if (!canSave) return;
    onSave({ field, value: value.trim(), neg });
    close();
  };

  return (
    <div className="relative inline-block" ref={ref}>
      {trigger(() => setOpen((o) => !o))}
      {open && (
        <div
          className="absolute z-50 left-0 top-full mt-1 w-[360px] rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="border-b border-border px-3 py-2">
            <h3 className="text-sm font-semibold">{isEdit ? "Editar filtro" : "Adicionar filtro"}</h3>
          </div>
          <div className="space-y-3 px-3 py-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Campo</label>
              <select
                value={field}
                onChange={(e) => setField(e.target.value)}
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {FILTER_FIELDS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Valor</label>
              <input
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                placeholder="ex.: 443 · BR · sec4us.com.br · *"
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <label className="inline-flex cursor-pointer select-none items-center gap-2 text-xs">
              <input type="checkbox" className="h-3.5 w-3.5" checked={neg} onChange={(e) => setNeg(e.target.checked)} />
              <span>Negar (excluir resultados que casam)</span>
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-border px-3 py-2">
            <button type="button" onClick={close} className="rounded-md px-3 py-1.5 text-xs hover:bg-muted/40">Cancelar</button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSave}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              Salvar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// EditableChip: chip que abre o editor ao clicar; X interno remove. Cláusulas
// complexas (OR/parênteses) não são editáveis — só removíveis.
function EditableChip({ filter, onReplace, onRemove }) {
  const chipBox = "inline-flex items-center gap-1 rounded-md border border-border bg-background py-0.5 pl-2 pr-1 text-xs shadow-sm";
  const removeBtn = (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onRemove(); }}
      aria-label="Remover filtro"
      title="Remover filtro"
      className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      <X className="h-3 w-3" />
    </button>
  );
  if (filter.complex) {
    return (
      <span className={chipBox} title={filter.raw}>
        <span className="text-muted-foreground">Expressão:</span>
        <span className="max-w-[280px] truncate font-medium">{filter.raw}</span>
        {removeBtn}
      </span>
    );
  }
  // negação: fundo/borda âmbar suaves + prefixo "NOT" em vermelho (em vez de ≠).
  const negStyle = filter.neg
    ? { backgroundColor: "rgba(251, 191, 36, 0.05)", borderColor: "rgba(251, 191, 36, 0.2)" }
    : undefined;
  return (
    <FilterEditor
      initial={filter}
      onSave={onReplace}
      trigger={(toggle) => (
        <span className={chipBox} style={negStyle}>
          <button type="button" onClick={toggle} className="inline-flex items-center gap-1 transition-colors hover:text-primary" title="Editar filtro">
            {filter.neg && <span className="font-semibold text-red-500">NOT</span>}
            <span className="text-muted-foreground">{fieldLabel(filter.field)}</span>
            <span className="text-muted-foreground">:</span>
            {(filter.value || "*") === "*" ? (
              <span className="font-medium italic text-muted-foreground">exists</span>
            ) : (
              <span className="max-w-[280px] truncate font-medium" title={filter.value}>{filter.value}</span>
            )}
          </button>
          {removeBtn}
        </span>
      )}
    />
  );
}

// FilterBar: barra de chips ativos + "Adicionar filtro". Deriva tudo de `query`;
// `onChange(novaQuery)` reescreve a busca.
function FilterBar({ query, onChange }) {
  const filters = parseFilters(query);
  const replaceAt = (i, nf) => { const next = [...filters]; next[i] = nf; onChange(buildQuery(next)); };
  const removeAt = (i) => onChange(buildQuery(filters.filter((_, j) => j !== i)));

  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-border bg-muted/30 px-2 py-1.5">
      <Filter className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
      <span className="mr-1 text-[11px] uppercase tracking-wider text-muted-foreground">Filtros</span>
      {filters.map((f, i) => (
        <EditableChip key={i} filter={f} onReplace={(nf) => replaceAt(i, nf)} onRemove={() => removeAt(i)} />
      ))}
      <FilterEditor
        onSave={(nf) => onChange(buildQuery([...filters, nf]))}
        trigger={(toggle) => (
          <button
            type="button"
            onClick={toggle}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-primary transition-colors hover:bg-primary/10"
            title="Adicionar filtro"
          >
            <Plus className="h-3.5 w-3.5" /> <span className="font-medium">Adicionar filtro</span>
          </button>
        )}
      />
      {filters.length > 1 && (
        <button type="button" onClick={() => onChange("")} className="ml-1 text-[11px] text-muted-foreground underline hover:text-foreground">
          Limpar todos
        </button>
      )}
    </div>
  );
}

const CHIP_CLICK = " cursor-pointer transition hover:brightness-125";

function HostCard({ h, onFilter }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/host/${h.ip}`} className="font-mono text-lg font-semibold text-primary hover:underline">
            {h.ip}
          </Link>
          {h.flags?.map((f, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onFilter(`${FLAG_FIELD[f.kind]}:${quote(f.label)}`)}
              title={`Filtrar por ${FLAG_FIELD[f.kind]}:${f.label}`}
              className={"inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium cursor-pointer transition hover:brightness-125 " + (FLAG_CHIP[f.kind] || "border-border bg-muted/40 text-muted-foreground")}
            >
              {f.label}
            </button>
          ))}
        </div>
        {(h.last_seen || h.observed_at || h.first_seen) && (
          <div className="shrink-0 text-right font-mono text-xs text-muted-foreground">
            {(h.last_seen || h.observed_at) && <div>{h.last_seen || h.observed_at}</div>}
            {h.first_seen && <div className="text-[10px] opacity-70">1ª vez: {h.first_seen}</div>}
          </div>
        )}
      </div>

      {(h.country || h.city || h.region) && (
        <div className="mt-1 flex flex-wrap items-center gap-x-1 text-sm text-muted-foreground">
          {h.country && <span className="mr-0.5">{flagEmoji(h.country)}</span>}
          {h.city && (
            <button type="button" onClick={() => onFilter(`city:${quote(h.city)}`)} title={`Filtrar por city:${h.city}`} className="hover:text-foreground hover:underline">
              {h.city}
            </button>
          )}
          {h.city && (h.region || h.country) && <span>,</span>}
          {h.region && (
            <button type="button" onClick={() => onFilter(`region:${quote(h.region)}`)} title={`Filtrar por region:${h.region}`} className="hover:text-foreground hover:underline">
              {h.region}
            </button>
          )}
          {h.region && h.country && <span>,</span>}
          {h.country && (
            <button type="button" onClick={() => onFilter(`country:${h.country}`)} title={`Filtrar por country:${h.country}`} className="hover:text-foreground hover:underline">
              {h.country}
            </button>
          )}
        </div>
      )}

      {h.fqdns?.length > 0 && (
        <CardSection title="Nomes DNS">
          {h.fqdns.map((f) => (
            <button key={f} type="button" onClick={() => onFilter(quote(f))} title={`Filtrar por ${f}`} className={DNS_CHIP + CHIP_CLICK}>
              {f}
            </button>
          ))}
        </CardSection>
      )}

      {(h.certs?.length > 0 || h.cert_names?.length > 0) && (() => {
        // Dedup por rótulo: o CN do cert e os FQDNs casados (cert_names) podem
        // coincidir (ex.: leaf CN == FQDN). Mantém o 1º (chip do cert, filtro ssl:).
        const seen = new Set();
        const chips = [];
        (h.certs || []).forEach((c, i) => {
          const name = c.cn || c.fingerprint;
          if (!name || seen.has(name)) return;
          seen.add(name);
          chips.push({ key: `c${i}`, label: name, filter: `ssl:${quote(name)}`, title: `Filtrar por ssl:${name}` });
        });
        (h.cert_names || []).forEach((n) => {
          if (!n || seen.has(n)) return;
          seen.add(n);
          chips.push({ key: `n${n}`, label: n, filter: quote(n), title: `Filtrar por ${n}` });
        });
        return (
          <CardSection title="Certificados">
            {chips.map((c) => (
              <button key={c.key} type="button" onClick={() => onFilter(c.filter)} title={c.title} className={CERT_CHIP + CHIP_CLICK}>
                <Lock className="h-3 w-3" />
                {c.label}
              </button>
            ))}
          </CardSection>
        );
      })()}

      {(() => {
        const isUnknown = (s) => !s.service || s.service.toLowerCase() === "unknown";
        const known = (h.services || []).filter((s) => !isUnknown(s));
        const other = (h.services || []).filter(isUnknown);
        return (
          <>
            {known.length > 0 && (
              <CardSection title="Serviços">
                {known.map((s, i) => (
                  <button key={i} type="button" onClick={() => onFilter(`port:${s.port}`)} title={`Filtrar por port:${s.port}`} className={PORT_CHIP + CHIP_CLICK}>
                    {s.port}/{s.service}
                  </button>
                ))}
              </CardSection>
            )}
            {other.length > 0 && (
              <CardSection title="Outros serviços">
                {other.map((s, i) => (
                  <button key={i} type="button" onClick={() => onFilter(`port:${s.port}`)} title={`Filtrar por port:${s.port}`} className={PORT_CHIP + CHIP_CLICK}>
                    {s.port}/{s.protocol || "tcp"}
                  </button>
                ))}
              </CardSection>
            )}
          </>
        );
      })()}
    </div>
  );
}

function PanelCard({ title, children }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  return String(d).slice(0, 10);
}

// Card resumido de certificado (aba "Certificados").
function CertCard({ c }) {
  return (
    <Link
      to={`/certificate/${c.fingerprint}`}
      className="block rounded-lg border border-border bg-card p-4 transition-colors hover:border-amber-400/40"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Lock className="h-4 w-4 shrink-0 text-amber-400/80" />
          <span className="truncate font-medium text-foreground">{c.cn || "(sem CN)"}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {c.self_signed && (
            <span className="rounded-md border border-amber-400/30 bg-amber-400/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-200/90">
              self-signed
            </span>
          )}
          {c.is_ca && (
            <span className="rounded-md border border-blue-500/30 bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-medium text-blue-300">
              CA
            </span>
          )}
        </div>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <div className="text-muted-foreground">
          <span className="text-foreground/60">Emissor:</span> {c.issuer_cn || c.issuer_org || "—"}
        </div>
        <div className="text-muted-foreground">
          <span className="text-foreground/60">Validade:</span> {fmtDate(c.not_before)} → {fmtDate(c.not_after)}
        </div>
        <div className="text-muted-foreground">
          <span className="text-foreground/60">Hosts:</span> {(c.host_count ?? 0).toLocaleString("pt-BR")}
        </div>
        <div className="text-muted-foreground">
          <span className="text-foreground/60">SANs:</span> {(c.san_count ?? 0).toLocaleString("pt-BR")}
        </div>
      </div>

      {c.san?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {c.san.map((s, i) => <span key={i} className={DNS_CHIP}>{s}</span>)}
        </div>
      )}

      <div className="mt-2 truncate font-mono text-[11px] text-muted-foreground/60">{c.fingerprint}</div>
    </Link>
  );
}

function parseTab(t) {
  return t === "certs" || t === "domains" ? t : "hosts";
}

// Card resumido de domínio (aba "Domínios").
function DomainCard({ d }) {
  return (
    <Link
      to={`/domain/${d.domain}`}
      className="block rounded-lg border border-border bg-card p-4 transition-colors hover:border-emerald-400/40"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Globe className="h-4 w-4 shrink-0 text-emerald-400/80" />
          <span className="truncate font-medium text-foreground">{d.domain}</span>
        </div>
        {d.last_seen && (
          <span className="shrink-0 font-mono text-xs text-muted-foreground">{String(d.last_seen).slice(0, 10)}</span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
        <span><span className="text-foreground/60">Subdomínios:</span> {(d.subdomain_count ?? 0).toLocaleString("pt-BR")}</span>
        <span><span className="text-foreground/60">Registros:</span> {(d.record_count ?? 0).toLocaleString("pt-BR")}</span>
      </div>
      {d.subdomains?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {d.subdomains.map((s) => <span key={s} className={DNS_CHIP}>{s}</span>)}
        </div>
      )}
    </Link>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "border-b-2 px-4 py-2 text-sm font-medium transition-colors " +
        (active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground")
      }
    >
      {children}
    </button>
  );
}

const PAGE_SIZE = 25; // igual ao size padrão do backend (/search)

function parseHashPage() {
  const m = /(?:^#|&)page=(\d+)/.exec(window.location.hash);
  return m ? Math.max(1, parseInt(m[1], 10)) : 1;
}

function setHashPage(p) {
  const base = `${window.location.pathname}${window.location.search}`;
  window.history.replaceState(null, "", p > 1 ? `${base}#page=${p}` : base);
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [tab, setTab] = useState(parseTab(searchParams.get("tab")));
  const [result, setResult] = useState(null);
  const [certResult, setCertResult] = useState(null);
  const [domainResult, setDomainResult] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const sentinelRef = useRef(null);
  const loadMoreRef = useRef(null);
  const pendingResetRef = useRef(false); // nova query => recomeça na página 1
  const targetCardRef = useRef(null); // card-âncora p/ rolar até a página do #page=
  const pendingScrollRef = useRef(null); // índice do card a rolar após carregar

  // Busca nos três índices (hosts + certificados + domínios). Para hosts, carrega
  // da página 1 até a página alvo (#page= na URL) acumulando — suporta F5.
  const doSearch = useCallback(async (query) => {
    setBusy(true);
    setError("");
    const targetPage = pendingResetRef.current ? 1 : parseHashPage();
    pendingResetRef.current = false;
    try {
      const [hostsRes, certsRes, domsRes] = await Promise.all([
        api.get("/search", { params: { q: query, page: 1 } }),
        api.get("/search/certs", { params: { q: query } }),
        api.get("/search/domains", { params: { q: query } }),
      ]);
      setResult(hostsRes.data);
      setCertResult(certsRes.data);
      setDomainResult(domsRes.data);

      let acc = hostsRes.data.hosts || [];
      const totalH = hostsRes.data.total ?? acc.length;
      if (targetPage > 1 && acc.length < totalH) {
        const more = await Promise.all(
          Array.from({ length: targetPage - 1 }, (_, i) =>
            api.get("/search", { params: { q: query, page: i + 2 } })
          )
        );
        more.forEach((r) => { acc = acc.concat(r.data.hosts || []); });
      }
      setHosts(acc);
      setPage(targetPage);
      setHasMore(acc.length < totalH);
      setHashPage(acc.length ? targetPage : 1);
      // marca o card-âncora p/ rolar até o início da página alvo (após render).
      pendingScrollRef.current =
        targetPage > 1 && acc.length
          ? Math.min((targetPage - 1) * PAGE_SIZE, acc.length - 1)
          : null;
    } catch (err) {
      setError(err.response?.data?.detail || "Falha na busca.");
      setResult(null);
      setCertResult(null);
      setDomainResult(null);
      setHosts([]);
      setHasMore(false);
    } finally {
      setBusy(false);
    }
  }, []);

  // Carrega a próxima página de hosts (scroll infinito) e reflete em #page=.
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || busy) return;
    setLoadingMore(true);
    const next = page + 1;
    try {
      const { data } = await api.get("/search", { params: { q, page: next } });
      const total = result?.total ?? 0;
      setHosts((prev) => {
        const merged = prev.concat(data.hosts || []);
        setHasMore(merged.length < total);
        return merged;
      });
      setPage(next);
      setHashPage(next);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, busy, page, q, result]);
  loadMoreRef.current = loadMore;

  // Observa o sentinel no fim da lista: ao aparecer, busca mais.
  useEffect(() => {
    if (tab !== "hosts" || !hasMore) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreRef.current?.(); },
      { rootMargin: "400px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [tab, hasMore, hosts.length]);

  // Após carregar até a página alvo (#page=), rola até o início dessa página.
  useEffect(() => {
    if (tab !== "hosts" || pendingScrollRef.current == null) return;
    if (!targetCardRef.current) return;
    const el = targetCardRef.current;
    pendingScrollRef.current = null;
    // pequeno atraso p/ garantir layout pronto antes de rolar.
    const t = setTimeout(() => el.scrollIntoView({ block: "start" }), 50);
    return () => clearTimeout(t);
  }, [tab, hosts.length]);

  // Toda navegação passa pela URL (fonte da verdade). As ações só atualizam a
  // URL; o efeito abaixo reage — inclusive a voltar/avançar do navegador.
  function pushQuery(nextQ, nextTab, opts) {
    pendingResetRef.current = true; // recomeça a paginação
    setHashPage(1); // limpa o #page=
    const params = {};
    if (nextQ) params.q = nextQ;
    if (nextTab && nextTab !== "hosts") params.tab = nextTab;
    setSearchParams(params, opts);
  }

  function run(e) {
    e?.preventDefault();
    pushQuery(q, tab);
  }

  // Clicar numa informação do resultado adiciona o dork à busca (campo + URL)
  // e re-executa. Ex.: "AND port:8080". Evita duplicar o mesmo termo.
  function addFilter(dork) {
    if (!dork) return;
    const next = q && q.includes(dork) ? q : (q ? `${q} AND ${dork}` : dork);
    if (next === q) return;
    pushQuery(next, tab);
  }

  function switchTab(next) {
    pushQuery(q, next, { replace: true });
  }

  // Sincroniza estado + resultados a partir da URL. Roda no mount, em F5 e
  // sempre que a URL muda (submit, clique em filtro, voltar/avançar).
  useEffect(() => {
    const urlQ = searchParams.get("q") || "";
    setQ(urlQ);
    setTab(parseTab(searchParams.get("tab")));
    if (urlQ) {
      doSearch(urlQ);
    } else {
      setResult(null);
      setCertResult(null);
      setDomainResult(null);
      setHosts([]);
      setHasMore(false);
      setPage(1);
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const searched = result !== null || certResult !== null || domainResult !== null;

  return (
    <div className="space-y-6">
      <form onSubmit={run} className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            placeholder='Ex.: nginx · port:443 · country:BR · ssl:sec4us.com.br · hostname:*.exemplo.com'
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="flex items-center gap-2 rounded-md bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <SearchIcon className="h-4 w-4" />}
          Buscar
        </button>
      </form>

      <div className="-mt-3">
        <FilterBar query={q} onChange={(nq) => pushQuery(nq, tab)} />
      </div>

      {error && <div className="rounded-md bg-destructive/15 px-3 py-2 text-sm text-destructive">{error}</div>}

      {searched && (
        <>
          {/* Abas: Hosts | Certificados */}
          <div className="flex gap-1 border-b border-border">
            <TabButton active={tab === "hosts"} onClick={() => switchTab("hosts")}>
              Hosts{result ? ` (${(result.total ?? 0).toLocaleString("pt-BR")})` : ""}
            </TabButton>
            <TabButton active={tab === "certs"} onClick={() => switchTab("certs")}>
              Certificados{certResult ? ` (${(certResult.total ?? 0).toLocaleString("pt-BR")})` : ""}
            </TabButton>
            <TabButton active={tab === "domains"} onClick={() => switchTab("domains")}>
              Domínios{domainResult ? ` (${(domainResult.total ?? 0).toLocaleString("pt-BR")})` : ""}
            </TabButton>
          </div>

          {tab === "hosts" && result && (
            <div className="flex flex-col gap-6 lg:flex-row">
              {/* Esquerda: cards por host (3/4) */}
              <div className="min-w-0 flex-1 space-y-3">
                {!hosts.length && (
                  <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground">
                    Nenhum host encontrado.
                  </div>
                )}
                {hosts.map((h, i) => (
                  <div key={h.ip} ref={i === pendingScrollRef.current ? targetCardRef : undefined}>
                    <HostCard h={h} onFilter={addFilter} />
                  </div>
                ))}

                {/* sentinel + indicadores do scroll infinito */}
                {hosts.length > 0 && (
                  <div ref={sentinelRef} className="py-4 text-center text-xs text-muted-foreground">
                    {loadingMore ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> carregando…
                      </span>
                    ) : hasMore ? (
                      "role para carregar mais"
                    ) : (
                      `fim — ${hosts.length.toLocaleString("pt-BR")} de ${(result.total ?? hosts.length).toLocaleString("pt-BR")}`
                    )}
                  </div>
                )}
              </div>

              {/* Direita: painel de métricas (1/4) */}
              <aside className="w-full shrink-0 space-y-4 lg:w-72">
                <PanelCard title="Total Results">
                  <div className="text-3xl font-semibold">{(result.total ?? 0).toLocaleString("pt-BR")}</div>
                  <div className="text-xs text-muted-foreground">IP(s)</div>
                </PanelCard>

                <PanelCard title="Top Countries">
                  <ResultsMap cities={result.cities || []} />
                  <div className="mt-2 space-y-1">
                    {(result.top_countries || []).length === 0 && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Globe className="h-3.5 w-3.5" /> sem dados de geo (reindexar com país/cidade)
                      </div>
                    )}
                    {(result.top_countries || []).map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => addFilter(`country:${c.key}`)}
                        title={`Filtrar por country:${c.key}`}
                        className="flex w-full items-center justify-between rounded px-1 text-sm hover:bg-muted/40"
                      >
                        <span>{flagEmoji(c.key)} {c.key}</span>
                        <span className="text-muted-foreground">{c.count.toLocaleString("pt-BR")}</span>
                      </button>
                    ))}
                  </div>
                </PanelCard>

                <PanelCard title="Top Ports">
                  {(result.top_ports || []).length === 0 && (
                    <div className="text-xs text-muted-foreground">—</div>
                  )}
                  <div className="space-y-1">
                    {(result.top_ports || []).map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => addFilter(`port:${p.key}`)}
                        title={`Filtrar por port:${p.key}`}
                        className="flex w-full items-center justify-between rounded px-1 text-sm hover:bg-muted/40"
                      >
                        <span className="font-mono">{p.key}</span>
                        <span className="text-muted-foreground">{p.count.toLocaleString("pt-BR")}</span>
                      </button>
                    ))}
                  </div>
                </PanelCard>

                <PanelCard title="Top Estados">
                  {(result.top_regions || []).length === 0 && (
                    <div className="text-xs text-muted-foreground">—</div>
                  )}
                  <div className="space-y-1">
                    {(result.top_regions || []).map((r) => (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => addFilter(`region:${quote(r.key)}`)}
                        title={`Filtrar por region:${r.key}`}
                        className="flex w-full items-center justify-between rounded px-1 text-sm hover:bg-muted/40"
                      >
                        <span className="truncate">{r.key}</span>
                        <span className="shrink-0 text-muted-foreground">{r.count.toLocaleString("pt-BR")}</span>
                      </button>
                    ))}
                  </div>
                </PanelCard>

                <PanelCard title="Top Cidades">
                  {(result.top_cities || []).length === 0 && (
                    <div className="text-xs text-muted-foreground">—</div>
                  )}
                  <div className="space-y-1">
                    {(result.top_cities || []).map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => addFilter(`city:${quote(c.key)}`)}
                        title={`Filtrar por city:${c.key}`}
                        className="flex w-full items-center justify-between rounded px-1 text-sm hover:bg-muted/40"
                      >
                        <span className="truncate">{c.key}</span>
                        <span className="shrink-0 text-muted-foreground">{c.count.toLocaleString("pt-BR")}</span>
                      </button>
                    ))}
                  </div>
                </PanelCard>
              </aside>
            </div>
          )}

          {tab === "certs" && (
            <div className="space-y-3">
              {!certResult?.certs?.length && (
                <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground">
                  Nenhum certificado encontrado.
                </div>
              )}
              {certResult?.certs?.map((c) => <CertCard key={c.fingerprint} c={c} />)}
            </div>
          )}

          {tab === "domains" && (
            <div className="space-y-3">
              {!domainResult?.domains?.length && (
                <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground">
                  Nenhum domínio encontrado.
                </div>
              )}
              {domainResult?.domains?.map((d) => <DomainCard key={d.domain} d={d} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
